export const BIN_ASSIGNMENT_MODES = {
  NEXT: "next",
  WEIGHT: "weight",
  QUANTITY: "quantity",
};

export const DEFAULT_BIN_ASSIGNMENT_MODE = BIN_ASSIGNMENT_MODES.NEXT;

export const BIN_ASSIGNMENT_OPTIONS = [
  {
    value: BIN_ASSIGNMENT_MODES.NEXT,
    label: "Next available bin",
    hint: "After QC, use the lowest-numbered empty bin.",
  },
  {
    value: BIN_ASSIGNMENT_MODES.WEIGHT,
    label: "By order weight",
    hint: "Small, Medium, or Large using the pound limits below. Each order goes in a bin in that range.",
  },
  {
    value: BIN_ASSIGNMENT_MODES.QUANTITY,
    label: "By order quantity",
    hint: "Pick the lowest empty bin whose max quantity covers the remaining units. Set a max quantity on each bin.",
  },
];

export const OZ_PER_LB = 16;
export const BIN_SIZE_SMALL_MAX_LB = 5;
export const BIN_SIZE_MEDIUM_MAX_LB = 20;
export const BIN_SIZE_SMALL_MAX_OZ = BIN_SIZE_SMALL_MAX_LB * OZ_PER_LB;
export const BIN_SIZE_MEDIUM_MAX_OZ = BIN_SIZE_MEDIUM_MAX_LB * OZ_PER_LB;

export const DEFAULT_BIN_SIZE_LIMITS = {
  smallMaxLb: BIN_SIZE_SMALL_MAX_LB,
  mediumMaxLb: BIN_SIZE_MEDIUM_MAX_LB,
};

export const BIN_SIZE_CLASSES = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
};

const parseLb = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 10) / 10;
};

export const formatLbAmount = (lb) => {
  const n = parseLb(lb);
  if (n == null) return "";
  return `${n} lb`;
};

export const ozToLb = (oz) => {
  const n = Number(oz);
  if (!Number.isFinite(n) || n <= 0) return null;
  return parseLb(n / OZ_PER_LB);
};

export const normalizeBinSizeLimits = (raw = {}) => {
  const small = parseLb(raw.smallMaxLb);
  const medium = parseLb(raw.mediumMaxLb);
  if (small != null && medium != null && medium > small) {
    return { smallMaxLb: small, mediumMaxLb: medium };
  }
  return { ...DEFAULT_BIN_SIZE_LIMITS };
};

export const binSizeClassOptions = (limits) => {
  const { smallMaxLb, mediumMaxLb } = normalizeBinSizeLimits(limits);
  return [
    {
      value: BIN_SIZE_CLASSES.SMALL,
      label: "Small",
      hint: `Max ${formatLbAmount(smallMaxLb)}`,
    },
    {
      value: BIN_SIZE_CLASSES.MEDIUM,
      label: "Medium",
      hint: `Max ${formatLbAmount(mediumMaxLb)}`,
    },
    {
      value: BIN_SIZE_CLASSES.LARGE,
      label: "Large",
      hint: `Over ${formatLbAmount(mediumMaxLb)}`,
    },
  ];
};

export const BIN_SIZE_CLASS_OPTIONS = binSizeClassOptions(DEFAULT_BIN_SIZE_LIMITS);

export const normalizeBinAssignmentMode = (mode) => {
  const value = String(mode || "")
    .trim()
    .toLowerCase();
  if (value === BIN_ASSIGNMENT_MODES.WEIGHT) return BIN_ASSIGNMENT_MODES.WEIGHT;
  if (value === BIN_ASSIGNMENT_MODES.QUANTITY)
    return BIN_ASSIGNMENT_MODES.QUANTITY;
  return BIN_ASSIGNMENT_MODES.NEXT;
};

export const normalizeBinSizeClass = (raw) => {
  const k = String(raw || "")
    .trim()
    .toLowerCase();
  if (k === BIN_SIZE_CLASSES.SMALL) return BIN_SIZE_CLASSES.SMALL;
  if (k === BIN_SIZE_CLASSES.MEDIUM) return BIN_SIZE_CLASSES.MEDIUM;
  if (k === BIN_SIZE_CLASSES.LARGE) return BIN_SIZE_CLASSES.LARGE;
  return "";
};

export const capsFromSizeClass = (sizeClass, limits) => {
  const k = normalizeBinSizeClass(sizeClass);
  const { smallMaxLb, mediumMaxLb } = normalizeBinSizeLimits(limits);
  const smallOz = smallMaxLb * OZ_PER_LB;
  const mediumOz = mediumMaxLb * OZ_PER_LB;
  if (k === BIN_SIZE_CLASSES.SMALL) {
    return {
      sizeClass: k,
      minWeightOz: null,
      maxWeightOz: smallOz,
    };
  }
  if (k === BIN_SIZE_CLASSES.MEDIUM) {
    return {
      sizeClass: k,
      minWeightOz: smallOz,
      maxWeightOz: mediumOz,
    };
  }
  if (k === BIN_SIZE_CLASSES.LARGE) {
    return {
      sizeClass: k,
      minWeightOz: mediumOz,
      maxWeightOz: null,
    };
  }
  return null;
};

export const sizeClassOfBin = (bin = {}) => {
  const explicit = normalizeBinSizeClass(bin?.sizeClass);
  if (explicit) return explicit;
  const min = Number(bin?.minWeightOz);
  const max = Number(bin?.maxWeightOz);
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max) && max > 0;
  if (!hasMin && hasMax) return BIN_SIZE_CLASSES.SMALL;
  if (hasMin && hasMax) return BIN_SIZE_CLASSES.MEDIUM;
  if (hasMin && !hasMax) return BIN_SIZE_CLASSES.LARGE;
  return "";
};

export const binSizeStamp = (sizeClass) => {
  const k = normalizeBinSizeClass(sizeClass);
  if (k === BIN_SIZE_CLASSES.SMALL) return "SMALL";
  if (k === BIN_SIZE_CLASSES.MEDIUM) return "MEDIUM";
  if (k === BIN_SIZE_CLASSES.LARGE) return "LARGE";
  return "";
};

export const binSizeWeightCopy = (sizeClass, bin = {}) => {
  const k = normalizeBinSizeClass(sizeClass);
  const limits = normalizeBinSizeLimits(bin);
  const storedMax = ozToLb(bin?.maxWeightOz);
  const storedMin = ozToLb(bin?.minWeightOz);
  if (k === BIN_SIZE_CLASSES.SMALL) {
    return `Max ${formatLbAmount(storedMax || limits.smallMaxLb)}`;
  }
  if (k === BIN_SIZE_CLASSES.MEDIUM) {
    return `Max ${formatLbAmount(storedMax || limits.mediumMaxLb)}`;
  }
  if (k === BIN_SIZE_CLASSES.LARGE) {
    return `Over ${formatLbAmount(storedMin || limits.mediumMaxLb)}`;
  }
  return "";
};

export const formatBinCapacity = (bin) => {
  const size = sizeClassOfBin(bin);
  if (size) {
    const stamp = binSizeStamp(size);
    const pretty = `${stamp.charAt(0)}${stamp.slice(1).toLowerCase()}`;
    return `${pretty} · ${binSizeWeightCopy(size, bin)}`;
  }
  const weight = Number(bin?.maxWeightOz);
  if (Number.isFinite(weight) && weight > 0) return `${weight} oz`;
  return "—";
};

/** Open orders need a confirm before a manual bin clear (cancel or skip). */
export const binClearRequiresOrderCancel = (bin) => {
  const orderId = String(bin?.orderId || "").trim();
  if (!orderId) return false;
  const order = bin?.orderDetails || {};
  if (order.cancelled === true) return false;
  if (order.shipped === true) return false;
  return true;
};

export const formatBinOrderLabel = (bin) => {
  const number =
    bin?.orderNumber ||
    bin?.orderDetails?.orderNumber ||
    bin?.platformOrderId ||
    bin?.orderDetails?.platformOrderId ||
    bin?.orderId;
  return number ? `#${number}` : "this order";
};

export const MAX_RACK_LAYOUT_BINS = 500;
export const MAX_RACK_SHELVES = 26;

const toPositiveInt = (value, fallback = 0) => {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const shelfLetterAt = (shelfFromBottom) => {
  const n = toPositiveInt(shelfFromBottom);
  if (!n || n > MAX_RACK_SHELVES) return "";
  return String.fromCharCode(64 + n);
};

/**
 * Names look like 1-1-A-1: rack, side (1 front / 2 back), shelf letter
 * from the floor (A bottom, B next, D fourth), then the slot to the right.
 */
export function rackBinNameAt({ rack, side = 1, shelf, slot }) {
  const rackN = toPositiveInt(rack);
  const sideN = toPositiveInt(side);
  const shelfN = toPositiveInt(shelf);
  const slotN = toPositiveInt(slot);
  const letter = shelfLetterAt(shelfN);
  if (!rackN || !sideN || !slotN || !letter) return "";
  return `${rackN}-${sideN}-${letter}-${slotN}`;
}

export function buildRackBinNames({
  firstRack = 1,
  rackCount = 1,
  shelvesHigh = 1,
  binsPerShelf = 1,
  hasBack = false,
} = {}) {
  const racks = toPositiveInt(firstRack);
  const howManyRacks = toPositiveInt(rackCount);
  const tall = toPositiveInt(shelvesHigh);
  const across = toPositiveInt(binsPerShelf);
  const sides = hasBack ? 2 : 1;
  if (!racks || !howManyRacks || !tall || !across) return [];
  if (tall > MAX_RACK_SHELVES) return [];
  const total = howManyRacks * sides * tall * across;
  if (total > MAX_RACK_LAYOUT_BINS) return [];
  const names = [];
  for (let r = 0; r < howManyRacks; r += 1) {
    for (let side = 1; side <= sides; side += 1) {
      for (let shelf = 1; shelf <= tall; shelf += 1) {
        for (let slot = 1; slot <= across; slot += 1) {
          names.push(
            rackBinNameAt({
              rack: racks + r,
              side,
              shelf,
              slot,
            })
          );
        }
      }
    }
  }
  return names.filter(Boolean);
}
